import { Component, OnInit, Input, EventEmitter, Output, OnChanges, signal, NO_ERRORS_SCHEMA } from '@angular/core';
import { ComissoesFilter, GruposFilter, OrganizacoesFilter, PatriarcasFilter, UnidadesFilter } from '../../default/fod-default.pipe';
import { OrganogramaService } from '../../../../../services/organograma.service';
import { Organizacao, Unidade } from '../../../../../models/organograma.model';
import { Enums } from '../../../../../shared/enums';
import { ToastrService } from 'ngx-toastr';
import { Patriarca } from '../../../../../models/edocs.model';
import { EDocsService } from '../../../../../services/edocs.service';
import { Utils } from '../../../../../shared/utils';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ConfigSchemaSectionEDocsLocation, LocationType } from '../../../../../models/config-schema.model';
import { IMPORTS } from './_imports';
import { LotacaoService } from '../../../../../services/lotacao.service';
import { Comissao, GrupoTrabalho } from '../../../../../models/lotacao.model';

@Component({
    selector: 'edocs-location',
    templateUrl: './edocs-location.component.html',
    styleUrl: './edocs-location.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class EDocsLocationComponent implements OnInit, OnChanges {
    // #region [Type props]
    Utils = Utils;
    // #endregion

    // #region [props]
    patriarcaId = '';
    patriarcas: Patriarca[] = [];
    organizacaoId = '';
    organizacoes: Organizacao[] = [];
    unidadeId = '';
    unidades: Unidade[] = [];
    grupoId = '';
    grupos: GrupoTrabalho[] = [];
    comissaoId = '';
    comissoes: Comissao[] = [];
    // #endregion

    // #region [signals]
    $model = signal(null as ConfigSchemaSectionEDocsLocation);
    $flowDefinitionOrganizationId = signal(null as string);
    $isReadOnlyMode = signal(false);
    $hideUnit = signal(false);
    $hideGroup = signal(false);
    $hideComission = signal(false);
    $hideSelectOrganization = signal(false);
    $patriarchLabel = signal(null as string);
    $organizationLabel = signal(null as string);
    $unitLabel = signal(null as string);
    $groupLabel = signal(null as string);
    $comissionLabel = signal(null as string);
    $indentLevel = signal(null as number);
    $patriarcaDisplay = signal('');
    $patriarcaNome = signal(null as string);
    $selectablePatriarcas = signal([] as Patriarca[]);
    $organizacaoDisplay = signal('');
    $organizacaoNome = signal(null as string);
    $selectableOrganizacoes = signal([] as Organizacao[]);
    $unidadeDisplay = signal('');
    $unidadeNome = signal(null as string);
    $selectableUnidades = signal([] as Unidade[]);
    $grupoDisplay = signal('');
    $grupoNome = signal(null as string);
    $selectableGrupos = signal([] as GrupoTrabalho[]);
    $comissaoDisplay = signal('');
    $comissaoNome = signal(null as string);
    $selectableComissoes = signal([] as Comissao[]);
    // #endregion

    // #region [Input/Output]
    @Input() _model: ConfigSchemaSectionEDocsLocation;
    @Input() _flowDefinitionOrganizationId: string;
    @Input() _isReadOnlyMode: boolean;
    @Input() _isConditionalForwarding: boolean;
    @Input() _hideUnit: boolean;
    @Input() _hideGroup: boolean;
    @Input() _hideComission: boolean;
    @Input() _hideSelectOrganization: boolean;
    @Input() _patriarchLabel: string;
    @Input() _organizationLabel: string;
    @Input() _unitLabel: string;
    @Input() _groupLabel: string;
    @Input() _comissionLabel: string;
    @Input() _indentLevel: number;
    @Output() _modelChange = new EventEmitter<ConfigSchemaSectionEDocsLocation>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private organogramaService: OrganogramaService,
        private edocsService: EDocsService,
        private lotacaoService: LotacaoService,
        private patriarcasFilter: PatriarcasFilter,
        private organizacoesFilter: OrganizacoesFilter,
        private unidadesFilter: UnidadesFilter,
        private gruposFilter: GruposFilter,
        private comissoesFilter: ComissoesFilter
    ) { }

    ngOnInit() {
        let interval = setInterval(async () => {
            if (this.$model() != null) {
                clearInterval(interval);

                const response_patriarcas = await this.edocsService.getPatriarcas();

                if (!response_patriarcas.isSuccess) {
                    this.toastr.error(response_patriarcas.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                this.patriarcas = response_patriarcas.data;
                this.patriarcas.sort((a, b) => {
                    let first = a.sigla || a.nome || '';
                    let second = b.sigla || b.nome || '';
                    return first.localeCompare(second);
                });
                Utils.setSignal(this.$selectablePatriarcas, this.patriarcas);
            }
        }, 100);
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowDefinitionOrganizationId.set(this._flowDefinitionOrganizationId);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$hideUnit.set(this._hideUnit);
        this.$hideGroup.set(this._hideGroup);
        this.$hideComission.set(this._hideComission);
        this.$hideSelectOrganization.set(this._hideSelectOrganization);
        this.$patriarchLabel.set(this._patriarchLabel);
        this.$organizationLabel.set(this._organizationLabel);
        this.$unitLabel.set(this._unitLabel);
        this.$groupLabel.set(this._groupLabel);
        this.$comissionLabel.set(this._comissionLabel);
        this.$indentLevel.set(this._indentLevel);
    }

    patriarcaDisplayChange() {
        this.$selectablePatriarcas.set(this.patriarcasFilter.transform(this.patriarcas, this.$patriarcaDisplay()));
    }

    async patriarcaIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.patriarcaId = event.option.value.id;
        }

        if (this.patriarcaId != '') {
            const response = await this.organogramaService.getOrganizacoesFilhas(this.patriarcaId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.organizacoes = response.data;
            this.organizacoes.sort((a, b) => {
                let first = a.sigla || a.nomeFantasia || a.razaoSocial || '';
                let second = b.sigla || b.nomeFantasia || b.razaoSocial || '';
                return first.localeCompare(second);
            });
            Utils.setSignal(this.$selectableOrganizacoes, this.organizacoes);
            this.$patriarcaNome.set(`${event.option.value.sigla} - ${event.option.value.nome}`.toUpperCase());
        } else {
            this.organizacaoId = '';
            this.organizacoes = [];
            this.$selectableOrganizacoes.set([]);
            this.$patriarcaNome.set(null);
            Utils.updSignal(this.$model, x => {
                x.name = null;
                x.id = null;
                return x;
            });
        }
    }

    displayPatriarcaId(option): string {
        return option != null && option != ''
            ? `${option.sigla} - ${option.nome.toUpperCase()}`
            : '';
    }

    clearPatriarca() {
        this.patriarcaId = '';
        this.$patriarcaDisplay.set('');
        Utils.setSignal(this.$selectablePatriarcas, this.patriarcas);
        this.organizacoes = [];
        this.clearOrganizacao();
    }

    organizacaoDisplayChange() {
        this.$selectableOrganizacoes.set(this.organizacoesFilter.transform(this.organizacoes, this.$organizacaoDisplay()));
    }

    async organizacaoIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.organizacaoId = event.option.value.guid;
        }

        if (this.organizacaoId != '') {
            let organization = this.organizacoes.find(x => x.guid == this.organizacaoId);
            let fullName = Utils.isNotNullOrEmpty(organization?.nomeFantasia) && organization.nomeFantasia != '.' ? organization.nomeFantasia : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(organization?.sigla) ? organization.sigla : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(organization?.organizacaoPai?.sigla) ? organization.organizacaoPai.sigla : '';
            this.$organizacaoNome.set((fullName.endsWith(' - ') ? fullName.slice(0, -3) : fullName).toUpperCase());

            let response = await this.organogramaService.getUnidadesOrganizacao(this.organizacaoId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.unidades = response.data;
            this.unidades.sort((a, b) => {
                let first = a.nomeCurto || a.nome || a.sigla || '';
                let second = b.nomeCurto || b.nome || b.sigla || '';
                return first.localeCompare(second);
            });
            Utils.setSignal(this.$selectableUnidades, this.unidades);

            response = await this.lotacaoService.getOrgaosGruposTrabalho(this.organizacaoId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.grupos = response.data;
            this.grupos.sort((a, b) => a.nome.localeCompare(b.nome));
            this.grupos.sort((a, b) => {
                let first = a.nome || '';
                let second = b.nome || '';
                return first.localeCompare(second);
            });
            Utils.setSignal(this.$selectableGrupos, this.grupos);

            response = await this.lotacaoService.getOrgaosComissoes(this.organizacaoId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.comissoes = response.data;
            this.comissoes.sort((a, b) => a.nome.localeCompare(b.nome));
            this.comissoes.sort((a, b) => {
                let first = a.nome || '';
                let second = b.nome || '';
                return first.localeCompare(second);
            });
            Utils.setSignal(this.$selectableComissoes, this.comissoes);
        }
    }

    displayOrganizacaoId(option): string {
        return Utils.isNotNullOrEmpty(option)
            ? `${option.sigla} - ${option.nomeFantasia == '.' ? option.sigla : option.nomeFantasia}`
            : '';
    }

    clearOrganizacao() {
        this.organizacaoId = '';
        this.$organizacaoDisplay.set('');
        this.$organizacaoNome.set(null);
        Utils.setSignal(this.$selectableOrganizacoes, this.organizacoes);
        this.unidades = [];
        this.clearUnidade();
        this.grupos = [];
        this.clearGrupo();
        this.comissoes = [];
        this.clearComissao();
    }

    selectOrganizacao() {
        if (this.isDisabledOrganizacao() || Utils.isNullOrEmpty(this.organizacaoId)) return;

        Utils.updSignal(this.$model, x => {
            x.name = this.$organizacaoNome();
            x.id = this.organizacaoId;
            x.type = LocationType.Organization;
            x.patriarchId = this.patriarcaId;
            x.patriarchName = this.$patriarcaNome();
            return x;
        });

        this._modelChange.emit(this.$model());

        if (!this._isConditionalForwarding) {
            this.clearPatriarca();
        }
    }

    isDisabledOrganizacao(): boolean {
        return this.$isReadOnlyMode() || this.organizacoes.length == 0;
    }

    unidadeDisplayChange() {
        this.$selectableUnidades.set(this.unidadesFilter.transform(this.unidades, this.$unidadeDisplay()));
    }

    unidadeIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.unidadeId = event.option.value.guid;
        }

        if (Utils.isNullOrEmpty(this.unidadeId)) {
            this.clearUnidade();
        } else {
            let organization = this.organizacoes.find(x => x.guid == this.organizacaoId);
            let unit = this.unidades.find(x => x.guid == this.unidadeId);
            let fullName = Utils.isNotNullOrEmpty(unit?.nome) ? unit.nome : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(unit?.nomeCurto) ? unit.nomeCurto : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(organization?.sigla) ? organization.sigla : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(organization?.organizacaoPai?.sigla) ? organization.organizacaoPai.sigla : '';
            this.$unidadeNome.set((fullName.endsWith(' - ') ? fullName.slice(0, -3) : fullName).toUpperCase());
        }
    }

    displayUnidadeId(option): string {
        return Utils.isNotNullOrEmpty(option)
            ? `${option.nomeCurto ? option.nomeCurto.toUpperCase() + ' - ' : ''}${option.nome.toUpperCase()}`
            : '';
    }

    clearUnidade() {
        this.unidadeId = '';
        this.$unidadeDisplay.set('');
        this.$unidadeNome.set(null);
        Utils.setSignal(this.$selectableUnidades, this.unidades);
    }

    selectUnidade() {
        if (this.isDisabledUnidade() || Utils.isNullOrEmpty(this.unidadeId)) return;

        Utils.updSignal(this.$model, x => {
            x.id = this.unidadeId;
            x.name = this.$unidadeNome();
            x.type = LocationType.Unit;
            x.flowInbox = `${this.$patriarcaNome().split(' - ')[0]} - ${this.$organizacaoNome().split(' - ')[1]} - ${this.$unidadeNome().split(' - ')[1]}`;
            x.patriarchId = this.patriarcaId;
            x.patriarchName = this.$patriarcaNome();
            return x;
        });

        this._modelChange.emit(this.$model());

        if (!this._isConditionalForwarding) {
            this.clearPatriarca();
        }
    }

    isDisabledUnidade(): boolean {
        return this.$isReadOnlyMode() || this.unidades.length == 0;
    }

    grupoDisplayChange() {
        this.$selectableGrupos.set(this.gruposFilter.transform(this.grupos, this.$grupoDisplay()));
    }

    grupoIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.grupoId = event.option.value.id;
        }

        if (Utils.isNullOrEmpty(this.grupoId)) {
            this.clearGrupo();
        } else {
            let organization = this.organizacoes.find(x => x.guid == this.organizacaoId);
            let group = this.grupos.find(x => x.id == this.grupoId);
            let fullName = Utils.isNotNullOrEmpty(group?.nome) ? group.nome : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(organization?.sigla) ? organization.sigla : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(organization?.organizacaoPai?.sigla) ? organization.organizacaoPai.sigla : '';
            this.$grupoNome.set((fullName.endsWith(' - ') ? fullName.slice(0, -3) : fullName).toUpperCase());
        }
    }

    displayGrupoId(option): string {
        return Utils.isNotNullOrEmpty(option)
            ? option.nome.toUpperCase()
            : '';
    }

    clearGrupo() {
        this.grupoId = '';
        this.$grupoDisplay.set('');
        this.$grupoNome.set(null);
        Utils.setSignal(this.$selectableGrupos, this.grupos);
    }

    selectGrupo() {
        if (this.isDisabledGrupo() || Utils.isNullOrEmpty(this.grupoId)) return;

        Utils.updSignal(this.$model, x => {
            x.id = this.grupoId;
            x.name = this.$grupoNome();
            x.type = LocationType.Group;
            x.patriarchId = this.patriarcaId;
            x.patriarchName = this.$patriarcaNome();
            return x;
        });

        this._modelChange.emit(this.$model());

        if (!this._isConditionalForwarding) {
            this.clearPatriarca();
        }
    }

    isDisabledGrupo(): boolean {
        return this.$isReadOnlyMode() || this.grupos.length == 0;
    }

    comissaoDisplayChange() {
        this.$selectableComissoes.set(this.comissoesFilter.transform(this.comissoes, this.$comissaoDisplay()));
    }

    comissaoIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.comissaoId = event.option.value.id;
        }

        if (Utils.isNullOrEmpty(this.comissaoId)) {
            this.clearComissao();
        } else {
            let organization = this.organizacoes.find(x => x.guid == this.organizacaoId);
            let comission = this.comissoes.find(x => x.id == this.comissaoId);
            let fullName = Utils.isNotNullOrEmpty(comission?.nome) ? comission.nome : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(organization?.sigla) ? organization.sigla : '';
            fullName += Utils.isNotNullOrEmpty(fullName) ? ' - ' : '';
            fullName += Utils.isNotNullOrEmpty(organization?.organizacaoPai?.sigla) ? organization.organizacaoPai.sigla : '';
            this.$comissaoNome.set((fullName.endsWith(' - ') ? fullName.slice(0, -3) : fullName).toUpperCase());
        }
    }

    displayComissaoId(option): string {
        return Utils.isNotNullOrEmpty(option)
            ? option.nome.toUpperCase()
            : '';
    }

    clearComissao() {
        this.comissaoId = '';
        this.$comissaoDisplay.set('');
        this.$comissaoNome.set(null);
        Utils.setSignal(this.$selectableComissoes, this.comissoes);
    }

    selectComissao() {
        if (this.isDisabledComissao() || Utils.isNullOrEmpty(this.comissaoId)) return;

        Utils.updSignal(this.$model, x => {
            x.id = this.comissaoId;
            x.name = this.$comissaoNome();
            x.type = LocationType.Comission;
            x.patriarchId = this.patriarcaId;
            x.patriarchName = this.$patriarcaNome();
            return x;
        });

        this._modelChange.emit(this.$model());

        if (!this._isConditionalForwarding) {
            this.clearPatriarca();
        }
    }

    isDisabledComissao(): boolean {
        return this.$isReadOnlyMode() || this.comissoes.length == 0;
    }

    // #region [===== private methods =====]
    // #endregion
}
